import Notification from '../../core/Notification';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import ExpandMore from '@material-ui/icons/ArrowDropDown';
import ExpandLess from '@material-ui/icons/ArrowDropUp';
import PersonIcon from '@material-ui/icons/Person';
import AssignmentIcon from '@material-ui/icons/Assignment';
import HistoryIcon from '@material-ui/icons/History';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import BasicReadField from '../../core/BasicReadField';
import CancelButton from '../../core/CancelButton';
import DiscussionViewer from '../../core/discussion/DiscussionViewer';
import Errored from '../../core/Errored';
import LanguageAvatar from '../../core/LanguageAvatar';
import Information from '../../core/Information';
import PrimaryButton from '../../core/PrimaryButton';
import { globalStyles } from '../../core/style/gstheme';
import { STATUS_ACCEPTED, STATUS_DRAFT, STATUS_PENDING, STATUS_REJECTED } from '../../core/utils/constants';
import { getAllLabelValues, makeCancelable } from '../../core/utils/utils';
import Warning from '../../core/Warning';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import { SUGGESTION_TYPE_OPTIONS } from '../constants';
import ConceptChip from '../core/ConceptChip';
import SchemeChip from '../core/SchemeChip';
import AddSuggestionNoteDialog from './AddSuggestionNoteDialog';

const styles = theme => {
    return Object.assign({
        accepted: {
            margin: '12px 0px 24px 0px',
            padding: '0px 12px 12px 12px',
            border: 'solid 1px #50832C',
            borderRadius: 4
        },
        rejected: {
            margin: '12px 0px 24px 0px',
            padding: '0px 12px 12px 12px',
            border: 'solid 1px #DD2C00',
            borderRadius: 4
        },
        suggestionIcon: {
            color: '#9E9E9E',
            height: 16,
            verticalAlign: 'bottom'
        },
        suggestionActionIcon: {
            '&:hover': {
                borderRadius: 4,
                backgroundColor: '#BDBDBD'
            }
        },
        suggestion: {
            boxShadow: '0px 1px 3px #00000033',
            borderRadius: 4,
            '&:hover': {
                boxShadow: '0px 12px 17px #00000033'
            }
        },
        expandedSuggestion: {
            boxShadow: '0px 1px 3px #00000033',
            borderRadius: 4
        },
        suggestionSummary: {
            height: 310,
            boxShadow: '0px 1px 3px #00000033',
            borderRadius: 4,
            '&:hover': {
                boxShadow: '0px 12px 17px #00000033'
            }
        },
        suggestionSummaryContainer: {
            padding: 12,
            borderRadius: 4,
            backgroundColor: '#f5f5f5'
        },
        suggestionTitle: {
            color: theme.palette.primary.main,
        },
        suggestionBorder: {
            color: 'green',
            border: 'none'
        },
        suggestionInnerText: {
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '22px',
            color: theme.palette.primary.main,
            padding: 0
        },
        suggestionText: {
            color: theme.palette.primary.main,
            fontSize: 16,
            fontWeight: 500,
            marginTop: 12,
            '& fieldset': {
                border: 'none'
            }
        },
        suggestionInnerTextBoxed: {
            color: '#212121',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '19px',
            marginTop: 12
        },
        suggestionSummaryHeader: {
            fontSize: 14,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            padding: '12px 12px 18px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        suggestionHeader: {
            fontSize: 14,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            backgroundColor: theme.palette.background.medium,
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        suggestionSubheader: {
            letterSpacing: '0.25px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        suggestionSubheaderOuter: {
            letterSpacing: '0.25px',
            width: '100%',
            display: 'flex',
            height: 30,
            alignItems: 'center',
            justifyContent: 'space-between'
        },
        suggestionInfo: {
            fontSize: 12,
            letterSpacing: '0.36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'left'
        },
        suggestionBody: {
            padding: 12
        },
        suggestionStatus: {
            paddingLeft: 12
        },
        buttonOptions: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
        }
    }, globalStyles);
};

class Suggestion extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: props.data.status && props.data.status[0] === STATUS_PENDING ? true : false,
            invalid: false,
            invalidReason: "",
            addNote: false,
            accepted: null,
            expanded: false,
            allowActions: false,
            exists: "created" in props.data
        };
        this.cancellable = null;
        this.cancellable2 = null;
        this.cancellable3 = null;
        this.cancellable4 = null;
    }

    componentDidMount() {
        if (this.props.data.status && this.props.data.status[0] === STATUS_PENDING) {
            this.loadConcepts();
            if (this.props.expanded) {
                this.expandSuggestion();
            }
        }
    }

    componentWillUnmount() {
        clearTimeout(this.waitTimeout);
        if (this.cancellable) this.cancellable.cancel();
        if (this.cancellable2) this.cancellable2.cancel();
        if (this.cancellable3) this.cancellable3.cancel();
        if (this.cancellable4) this.cancellable4.cancel();
    }

    // There might be many reasons why a suggestion no longer applies
    // We load the relevent concept data and apply rules here
    async loadConcepts() {
        let resources = [];
        if (this.props.data.suggestionTargetConcept) resources.push(this.props.data.suggestionTargetConcept);
        if (this.props.data.suggestionSourceConcept) resources.push(this.props.data.suggestionSourceConcept);
        if (resources.length === 0) return;
        try {
            const suggestionType = this.props.data.type;
            await this.loadData(resources).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            resources.forEach(resource => {
                const data = GS.data(resource, this.props.graph, false);
                let invalidReason = "";
                if (data && data.id === this.props.data.suggestionTargetConcept) {
                    const isInScheme = data.inScheme && data.inScheme.includes(this.props.schemeIRI) ? true : false;
                    if (data.deprecated || data.pendingDeletion || !isInScheme) {
                        if (!isInScheme) {
                            invalidReason = "the target not being in this concept scheme.";
                        }
                        else if (data.deprecated) {
                            invalidReason = "the target being deprecated.";
                        }
                        else {
                            invalidReason = "the target being deleted.";
                        }
                    }
                    if (this.props.useWorkflow && data.status && data.status[0] === STATUS_REJECTED) {
                        invalidReason = "the target being rejected.";
                    }
                }
                if (invalidReason === "" && data && data.id === this.props.data.suggestionSourceConcept) {
                    const isInScheme = this.props.data.suggestionSourceConcept === this.props.schemeIRI ?
                        true
                        :
                        data.inScheme && data.inScheme.includes(this.props.schemeIRI) ?
                            true
                            :
                            false;
                    if ((suggestionType === "RemovalSuggestion" || suggestionType === "MovingSuggestion") && this.props.data.suggestionParentConcept
                        && (!data.broader || !data.broader.includes(this.props.data.suggestionParentConcept))) {
                        invalidReason = "the broader concept no longer being a broader concept.";
                    }
                    else if (suggestionType === "MovingSuggestion" &&
                        data.broader && data.broader.includes(this.props.data.suggestionTargetConcept)) {
                        invalidReason = "the concept having already been moved to the target.";
                    }
                    else if (data.deprecated || data.pendingDeletion || !isInScheme) {
                        if (!isInScheme) {
                            invalidReason = this.props.schemeIRI ?
                                "the source not being in this concept scheme."
                                :
                                "not being editable in this view.";
                        }
                        else if (data.deprecated) {
                            invalidReason = "the source being deprecated.";
                        }
                        else {
                            invalidReason = "the source being deleted.";
                        }
                    }
                    if (invalidReason === "" && (suggestionType === "SynonymSuggestion" || suggestionType === "LabelSuggestion")) {
                        const label = suggestionType === "SynonymSuggestion" ? this.props.data.altLabel : this.props.data.prefLabel;
                        if (label) {
                            const lang = Object.keys(label);
                            const value = label[lang];
                            const allLabels = getAllLabelValues(data, lang);
                            const isDuplicate = allLabels.some(al => al.toLowerCase() === value.toLowerCase());
                            if (isDuplicate) {
                                invalidReason = "the label already existing on the concept.";
                            }
                        }
                    }
                    if (this.props.useWorkflow && data.status && data.status[0] === STATUS_REJECTED) {
                        invalidReason = "the source being rejected.";
                    }
                }
                if (invalidReason !== "") {
                    this.setState({
                        invalid: true,
                        invalidReason
                    });
                }
            });
            this.setState({ isLoading: false });
        }
        catch (e) {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
    }

    loadData(data) {
        this.cancellable = makeCancelable(GS.loadData(data, {}, this.props.graph, true));
        return this.cancellable;
    }

    doPath(payload) {
        this.cancellable3 = makeCancelable(GS.path(payload));
        return this.cancellable3;
    }

    doSearch(payload) {
        this.cancellable4 = makeCancelable(GS.search(payload));
        return this.cancellable4;
    }

    doFind(payload) {
        this.cancellable2 = makeCancelable(GS.find(payload));
        return this.cancellable2;
    }

    findIRI = async (value) => {
        try {
            const data = await this.doFind({
                value,
                graph: this.props.graph
            }).promise;
            return data && data.exists;
        }
        catch (e) {
            if (this.cancellable2 && !this.cancellable2.isCancelled()) this.setState({ isErrored: e });
            return null;
        }
    };

    isWaiting = async () => {
        if (this.waitTimeout) clearTimeout(this.waitTimeout);
        const exists = await this.findIRI(this.props.id);
        if (exists) {
            this.setState({ "exists": true });
        }
        else {
            this.waitTimeout = setTimeout(() => this.isWaiting(), 1000);
        }
    };

    checkLabel = async (value, lang) => {
        try {
            const data = await this.doSearch(
                {
                    graph: [this.props.graph],
                    search: [
                        {
                            predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                            value: this.props.schemeIRI,
                        },
                        {
                            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                            value: "http://www.w3.org/2004/02/skos/core#Concept"
                        },
                        {
                            predicate: [
                                "http://www.w3.org/2004/02/skos/core#prefLabel"
                            ],
                            value: value,
                            isExactMatch: false
                        }
                    ],
                    language: lang,
                    predicates: [
                        "http://www.w3.org/2004/02/skos/core#prefLabel"
                    ],
                    entailment: ["propertyChain"]
                }
            ).promise;
            if (this.cancellable4 && this.cancellable4.isCancelled()) return null;
            const isDuplicate = data.results ?
                data.results.some(result => {
                    // Just in case there are multiple pref labels
                    const labels = Array.isArray(result.prefLabel) ?
                        result.prefLabel
                        :
                        [result.prefLabel];
                    return labels.some(label => label[lang].toLowerCase() === value.toLowerCase());
                })
                :
                false;
            return isDuplicate;
        }
        catch (e) {
            if (this.cancellable4 && !this.cancellable4.isCancelled()) this.setState({ isErrored: e });
            return null;
        }
    };

    checkConcept = async (id) => {
        try {
            const broader = await this.doPath({
                graph: this.props.graph,
                source: this.props.data.suggestionTargetConcept,
                path: "http://www.w3.org/2004/02/skos/core#broader"
            }).promise;
            if (this.cancellable3 && this.cancellable3.isCancelled()) return null;
            if (broader.results) {
                const isAlreadyBroader = broader.results.some(result => result.parent === id);
                if (isAlreadyBroader) return "You cannot currently accept this suggestion as the move would introduce a circular issue in the hierarchy.";
            }
            return "";
        }
        catch (e) {
            if (this.cancellable3 && !this.cancellable3.isCancelled()) this.setState({ isErrored: e });
            return null;
        }
    };

    onExitWarning = () => this.setState({ isWarning: false });

    computeLink = (conceptIRI) => {
        const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + this.props.projectIRI + "&taxonomy="
            + this.props.schemeIRI + "&view=concept&concept=" + conceptIRI;
        return link;
    };

    handleChangeAllowAction = (event) => this.setState({ allowActions: event.target.checked });

    onSelect = (link) => {
        this.props.history.push(link);
        if (this.props.onToggleView) this.props.onToggleView();
    };

    resolveSuggestion = async (lang, text, useXL = true, resourceIRI, labelResourceIRI) => {
        if (this.props.data.type === "MovingSuggestion") {
            const checkResult = await this.checkConcept(this.props.data.suggestionSourceConcept);
            if (checkResult === null) return;
            if (checkResult !== "") {
                this.setState({ isWarning: true, errorMessage: checkResult, addNote: false });
                if (this.props.onResolution) this.props.onResolution();
                return;
            }
        }
        else if (this.props.data.type === "NewConceptSuggestion") {
            const labelLang = Object.keys(this.props.data.prefLabel)[0];
            // We need to check the label is still valid (IRI will be reserved)
            const isDuplicate = await this.checkLabel(this.props.data.prefLabel[labelLang], labelLang);
            if (this.cancellable4 && this.cancellable4.isCancelled()) return;
            if (isDuplicate === null) {
                this.setState({ addNote: false });
                if (this.props.onResolution) this.props.onResolution();
                return;
            }
            if (isDuplicate) {
                this.props.handleStatusChange(STATUS_REJECTED);
                this.setState({
                    isWarning: true,
                    errorMessage: "You cannot accept this suggestion as the new concept would have the same preferred label as an existing concept. It will be rejected instead."
                });
                this.rejectSuggestion("en", "Automatically rejected due to a clash with an existing label.");
                this.setState({ addNote: false });
                if (this.props.onResolution) this.props.onResolution();
                return;
            }
            const updateXL = "useXL" in this.props.data ?
                this.props.data.useXL !== useXL
                :
                useXL;
            // Make any updates to the suggestion
            if (resourceIRI !== this.props.data.suggestionInsertIRI) {
                if (!this.props.data.suggestionInsertIRI) {
                    this.props.onAdd("suggestionInsertIRI", resourceIRI);
                }
                else {
                    this.props.onUpdate("suggestionInsertIRI", resourceIRI);
                }
            }
            if (labelResourceIRI && labelResourceIRI !== this.props.data.suggestionLabelInsertIRI) {
                if (!this.props.data.suggestionLabelInsertIRI) {
                    this.props.onAdd("suggestionLabelInsertIRI", labelResourceIRI);
                }
                else {
                    this.props.onUpdate("suggestionLabelInsertIRI", labelResourceIRI);
                }
            }
            if (this.props.optionForXL && updateXL) {
                if ("useXL" in this.props.data) {
                    this.props.onUpdate("useXL", useXL.toString());
                }
                else {
                    this.props.onAdd("useXL", useXL.toString());
                }
            }
        }
        else if (this.props.data.type === "SynonymSuggestion" || this.props.data.type === "LabelSuggestion") {
            // If there is a clash of labels will be rejected by backend
            // But the UI stops acceptance if clashing so chances of getting past that anyway negligible
            if (this.props.data.type === "LabelSuggestion") {
                const labelLang = Object.keys(this.props.data.prefLabel)[0];
                const isDuplicate = await this.checkLabel(this.props.data.prefLabel[labelLang], labelLang);
                if (this.cancellable4 && this.cancellable4.isCancelled()) return;
                if (isDuplicate === null) {
                    this.setState({ addNote: false });
                    if (this.props.onResolution) this.props.onResolution();
                    return;
                }
                if (isDuplicate) {
                    this.props.handleStatusChange(STATUS_REJECTED);
                    this.setState({
                        isWarning: true,
                        errorMessage: "You cannot accept this suggestion as the new label would clash with an existing label on another concept. It will be rejected instead."
                    });
                    this.rejectSuggestion("en", "Automatically rejected due to a clash with an existing label.");
                    this.setState({ addNote: false });
                    if (this.props.onResolution) this.props.onResolution();
                    return;
                }
            }
            const updateXL = "useXL" in this.props.data ?
                this.props.data.useXL !== useXL
                :
                useXL;
            if (labelResourceIRI && labelResourceIRI !== this.props.data.suggestionLabelInsertIRI) {
                if (!this.props.data.suggestionLabelInsertIRI) {
                    this.props.onAdd("suggestionLabelInsertIRI", labelResourceIRI);
                }
                else {
                    this.props.onUpdate("suggestionLabelInsertIRI", labelResourceIRI);
                }
            }
            if (this.props.optionForXL && updateXL) {
                if ("useXL" in this.props.data) {
                    this.props.onUpdate("useXL", useXL.toString());
                }
                else {
                    this.props.onAdd("useXL", useXL.toString());
                }
            }
        }
        if (lang && text) this.props.onAdd('suggestionAcceptanceNote', text, lang);
        if (this.props.useWorkflow) {
            this.props.onAdd('suggestionAcceptanceStatus', STATUS_DRAFT);
        }
        if (!this.props.data.suggestionAcceptanceUser || !this.props.data.suggestionAcceptanceUser.includes(this.props.user)) {
            this.props.onAdd('suggestionAcceptanceUser', this.props.user);
        }
        const status = this.props.data.status ? this.props.data.status[0] : null;
        status ?
            this.props.onUpdate("status", STATUS_ACCEPTED, null, 0)
            :
            this.props.onAdd("status", STATUS_ACCEPTED);
        this.setState({ addNote: false });
        if (this.props.onResolution) this.props.onResolution();
    };

    rejectSuggestion = (lang, text) => {
        if (lang && text) this.props.onAdd('suggestionRejectionNote', text, lang);
        if (!this.props.data.suggestionRejectionUser || !this.props.data.suggestionRejectionUser.includes(this.props.user)) {
            this.props.onAdd('suggestionRejectionUser', this.props.user);
        }
        const status = this.props.data.status ? this.props.data.status[0] : null;
        status ?
            this.props.onUpdate("status", STATUS_REJECTED, null, 0)
            :
            this.props.onAdd("status", STATUS_REJECTED);
        this.setState({ addNote: false });
        if (this.props.onResolution) this.props.onResolution();
    };

    onCancelNote = () => {
        this.setState({ addNote: false });
    };

    onSkipNote = (useXL, resourceIRI, labelResourceIRI) => {
        this.onAddNote(null, null, useXL, resourceIRI, labelResourceIRI);
    };

    onAddNote = (lang, text, useXL, resourceIRI, labelResourceIRI) => {
        if (this.state.accepted) {
            this.resolveSuggestion(lang, text, useXL, resourceIRI, labelResourceIRI);
        }
        else {
            this.rejectSuggestion(lang, text);
        }
    };

    expandSuggestion = async () => {
        // We need to check the suggestion exists before we can action it so, if pending we do a check
        const exists = this.state.exists;
        this.setState({ expanded: true, exists }, () => {
            if (!exists) {
                const status = this.props.data.status ? this.props.data.status[0] : null;
                if (status === STATUS_PENDING) {
                    this.waitTimeout = setTimeout(() => this.isWaiting(), 1000);
                }
            }
        });
    };

    collapseSuggestion = () => this.setState({ expanded: false });

    onPromptForNote = (accepted) => this.setState({ accepted, addNote: true });

    render() {

        const { classes, data, users, graph, defaultLang, projectIRI, showDiscussion, iriOptions, useWorkflow, useCounterIRI, counters, useSlashIRI,
            isReadOnly, entitlements, schemeIRI, components, optionForXL, useXL, resourceStatus } = this.props;
        const { isErrored, invalid, invalidReason, errorMessage, isWarning, addNote, expanded, accepted, allowActions, exists } = this.state;
        const { comment, created, user, type, hasDiscussion, status,
            suggestionTargetConcept, suggestionParentConcept, prefLabel, altLabel } = data;

        const userId = Array.isArray(user) ? user[0] : user;
        const date = created ? new Date(created) : null;
        const dateText = date ? date.toLocaleString() : "";
        const userData = users.find(user => user.value === userId);
        const assignedUser = data.assignedUser ?
            users.find(user => user.value === data.assignedUser)
            :
            null;
        const userText = userData ?
            userData.label
            :
            "User not available";

        const assignedUserText = assignedUser ?
            assignedUser.label
            :
            "User not available";
        const assignedToUser = assignedUser ? userId === assignedUser.value : true;
        const suggestionType = SUGGESTION_TYPE_OPTIONS.find(option => option.value === type);
        const title = suggestionType.label;
        const explanation = suggestionType.explanation;
        const targetMandatory = suggestionType.targetMandatory;
        const commentLang = comment ? Object.keys(comment)[0] : defaultLang;
        const labelLang = prefLabel ?
            Object.keys(prefLabel)[0]
            :
            altLabel ?
                Object.keys(altLabel)[0]
                :
                null;
        // Check that the suggestion is for this taxonomy
        const isInScheme = data.inScheme && data.inScheme.includes(schemeIRI) ? true : false;
        const pending = status[0] === STATUS_PENDING;

        const disabled = invalid || (targetMandatory && !suggestionTargetConcept);
        let canBeRejected = entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS)
            && exists && (assignedToUser || allowActions)
            && (isInScheme || !data.inScheme || data.inScheme.length === 0);
        let canBeAccepted = entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS)
            && !disabled && exists && (assignedToUser || allowActions) && isInScheme
            && !(useWorkflow && resourceStatus === STATUS_ACCEPTED);

        const currentStatus = status[0];
        const notes = currentStatus === STATUS_ACCEPTED ?
            data.suggestionAcceptanceNote
            :
            data.suggestionRejectionNote;
        const statusUsers = currentStatus === STATUS_ACCEPTED ?
            data.suggestionAcceptanceUser
            :
            data.suggestionRejectionUser;

        return (
            <div className={this.props.expanded ? classes.expandedSuggestion : classes.suggestion}>
                <div data-test={"suggestion-" + title}>
                    <div className={classes.suggestionHeader}>
                        <div className={classes.suggestionSubheaderOuter}>
                            <div className={classes.suggestionSubheader}>
                                <div className={classes.suggestionTitle}>
                                    {title}
                                </div>
                                <div className={classes.suggestionStatus}>
                                    {
                                        currentStatus === STATUS_ACCEPTED ?
                                            <span data-test="accepted-status" style={{ color: '#50832C' }}>Accepted</span>
                                            :
                                            currentStatus === STATUS_REJECTED ?
                                                <span data-test="rejected-status" style={{ color: '#DD2C00' }}>Rejected</span>
                                                :
                                                <span data-test="pending-status" style={{ color: '#CA4F0C' }}>Open</span>
                                    }
                                </div>
                                <div data-test={data.assignedUser ? "assigned-user" : "unassigned"} style={{
                                    marginRight: 12,
                                    flex: 1, textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden'
                                }}>
                                    {data.assignedUser &&
                                        <Tooltip title={"Assigned to " + assignedUserText}>
                                            <span>
                                                <AssignmentIcon className={classes.suggestionIcon} />
                                                <span data-test="assigned-user-text">
                                                    {assignedUserText}
                                                </span>
                                            </span>
                                        </Tooltip>
                                    }
                                </div>
                            </div>
                        </div>
                        {!this.props.expanded &&
                            <div>
                                {expanded ?
                                    <Tooltip title="Show less detail">
                                        <ExpandLess className={classes.suggestionActionIcon} data-test="collapse" color="primary" onClick={this.collapseSuggestion} />
                                    </Tooltip>
                                    :
                                    <Tooltip title="Show full suggestion detail">
                                        <ExpandMore className={classes.suggestionActionIcon} data-test="expand" color="primary" onClick={this.expandSuggestion} />
                                    </Tooltip>
                                }
                            </div>
                        }
                    </div>
                    <div className={classes.suggestionBody}>
                        <div>
                            {pending && targetMandatory && !suggestionTargetConcept ?
                                <Information margin={true} dataTest="target-deleted" text="This suggestion is disabled due to the target for the suggestion being deleted." />
                                :
                                invalid &&
                                <Information margin={true} dataTest="suggestion-disabled" text={
                                    invalidReason === "" ?
                                        "This suggestion is disabled due to its relations to other concepts."
                                        :
                                        "This suggestion is disabled due to " + invalidReason
                                } />
                            }
                            {(!data.inScheme || data.inScheme.length === 0) &&
                                <Information margin={true} dataTest="scheme-deleted" text="This suggestion is from a deleted concept scheme." />
                            }
                        </div>
                        <div className={classes.suggestionSummaryContainer}>
                            <div className={classes.suggestionInfo}>
                                <div data-test="comment-lang">
                                    <LanguageAvatar lang={commentLang} />
                                </div>
                                <div data-test="suggestion-user" style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                    <PersonIcon className={classes.suggestionIcon} />
                                    {userText}
                                </div>
                                <div data-test="suggestion-date" style={{ paddingLeft: 12, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                    <HistoryIcon className={classes.suggestionIcon} />
                                    {dateText || "Recent"}
                                </div>
                            </div>
                            {comment &&
                                <TextField
                                    name="Suggested text"
                                    multiline={true}
                                    variant={"outlined"}
                                    fullWidth={true}
                                    InputProps={{
                                        inputProps: {
                                            "aria-label": "Suggested text",
                                            "data-test": "suggestion-text"
                                        },
                                        className: classes.suggestionInnerText
                                    }
                                    }
                                    className={classes.suggestionText}
                                    data-test="suggestion-text-field"
                                    value={comment[commentLang]} />}
                            {!isInScheme && data.inScheme && components.includes(data.inScheme[0]) &&
                                <React.Fragment>
                                    <Typography variant="body2" style={{ color: '#13294B', marginBottom: '6px' }}>This suggestion applies to the following concept scheme:</Typography>
                                    <SchemeChip
                                        id={data.inScheme[0]}
                                        defaultLang={defaultLang}
                                        isReadOnly={true}
                                        graph={graph}
                                        property="inScheme"
                                        link={
                                            APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                                            + projectIRI + "&taxonomy=" + data.inScheme[0]
                                        } />
                                </React.Fragment>
                            }
                            {prefLabel &&
                                <div data-test="new-concept-label" style={{ margin: '12px 0px' }}>
                                    <Typography variant="body2" style={{ color: '#13294B', marginBottom: '6px' }}>Suggested Label</Typography>
                                    <BasicReadField ariaLabel="Suggested label" lang={labelLang} dataTest="suggested-label" fullWidth text={prefLabel[labelLang]} />
                                </div>
                            }
                            {altLabel &&
                                <div data-test="new-concept-label" style={{ margin: '12px 0px' }}>
                                    <Typography variant="body2" style={{ color: '#13294B', marginBottom: '6px' }}>Suggested Label</Typography>
                                    <BasicReadField ariaLabel="Suggestion label" lang={labelLang} dataTest="suggested-label" fullWidth text={altLabel[labelLang]} />
                                </div>
                            }
                            {suggestionParentConcept &&
                                <div data-test="parent-concept" style={{ margin: '12px 0px' }}>
                                    <Typography variant="body2" style={{ color: '#13294B', marginBottom: '6px' }}>Broader concept for this action:</Typography>
                                    <ConceptChip
                                        id={suggestionParentConcept}
                                        onSelect={() => this.onSelect(this.computeLink(suggestionParentConcept))}
                                        graph={graph}
                                        loadOptions={{
                                            language: defaultLang
                                        }}
                                        defaultLang={defaultLang} />
                                </div>
                            }
                            {suggestionTargetConcept &&
                                <div data-test="target-concept" style={{ margin: '12px 0px' }}>
                                    <Typography variant="body2" style={{ color: '#13294B', marginBottom: '6px' }}>{explanation || "Target concept"}:</Typography>
                                    <ConceptChip
                                        id={suggestionTargetConcept}
                                        graph={graph}
                                        onSelect={() => this.onSelect(this.computeLink(suggestionTargetConcept))}
                                        loadOptions={{
                                            language: defaultLang
                                        }}
                                        defaultLang={defaultLang} />
                                </div>
                            }
                            {expanded && !isReadOnly && currentStatus === STATUS_PENDING &&
                                <div style={{ marginTop: 12 }}>
                                    <div className={classes.buttonOptions}>
                                        {!assignedToUser && isInScheme &&
                                            <div style={allowActions ? { marginRight: 24 } : {}}>
                                                <Typography variant="caption">
                                                    <span style={{ marginRight: 4 }}>Assigned to another user. Allow actions?</span>
                                                    <Checkbox
                                                        data-test="allow-actions"
                                                        checked={allowActions}
                                                        onChange={this.handleChangeAllowAction}
                                                        name="allowActions" />
                                                </Typography>
                                            </div>
                                        }
                                        {!exists &&
                                            <div>
                                                <Notification
                                                    text="Waiting for suggestion status."
                                                    dataTest="waiting-for-suggestion" />
                                            </div>
                                        }
                                        {canBeRejected &&
                                            <div style={{ marginRight: 12 }}>
                                                {!disabled ?
                                                    <CancelButton
                                                        dataTest="reject-button"
                                                        onClick={() => this.onPromptForNote(false)}>Reject</CancelButton>
                                                    :
                                                    <PrimaryButton
                                                        dataTest="primary-reject-button"
                                                        onClick={() => this.onPromptForNote(false)}>Reject</PrimaryButton>
                                                }
                                            </div>
                                        }
                                        {canBeAccepted &&
                                            <PrimaryButton onClick={() => this.onPromptForNote(true)}>Accept</PrimaryButton>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                        {expanded && <div data-test="expanded-panel" className={classes.expandedBody}>
                            {(currentStatus === STATUS_ACCEPTED || currentStatus === STATUS_REJECTED) &&
                                <div className={currentStatus === STATUS_ACCEPTED ?
                                    classes.accepted
                                    :
                                    classes.rejected}>
                                    {notes ?
                                        Object.keys(notes).map((lang) => {
                                            const langNotes = Array.isArray(notes[lang]) ? notes[lang] : [notes[lang]];
                                            return langNotes.map((note, i) =>
                                                <div style={{ marginTop: 12 }} key={lang + "-" + i}>
                                                    <div className={classes.suggestionInfo}>
                                                        <div>
                                                            <LanguageAvatar data-test="justification-lang" lang={lang} />
                                                        </div>
                                                        <div data-test="suggestion-user">
                                                            <span style={{
                                                                marginLeft: 12,
                                                                color: currentStatus === STATUS_ACCEPTED ? '#50832C' : '#DD2C00'
                                                            }}>
                                                                {currentStatus === STATUS_ACCEPTED ? "Accepted by " : "Rejected by "}
                                                            </span>
                                                            {statusUsers ?
                                                                statusUsers.map((statusUser, statusIndex) => {
                                                                    const statusUserData = users.find(user => user.value === statusUser);
                                                                    const statusUserText = statusUserData ?
                                                                        statusUserData.label
                                                                        :
                                                                        "User not available";
                                                                    return <span key={statusIndex} data-test="status-user" style={{ marginRight: 12 }}>
                                                                        <PersonIcon className={classes.suggestionIcon} />
                                                                        {statusUserText}
                                                                    </span>;
                                                                })
                                                                :
                                                                <span data-test="status-user" style={{ marginRight: 12 }}><PersonIcon className={classes.suggestionIcon} /> API</span>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <TextField
                                                            multiline={true}
                                                            variant={"outlined"}
                                                            fullWidth={true}
                                                            InputProps={{
                                                                inputProps: {
                                                                    "data-test": "justification-text"
                                                                },
                                                                className: classes.suggestionInnerText,
                                                            }}
                                                            className={classes.suggestionText}
                                                            value={note} />
                                                    </div>
                                                </div>
                                            );
                                        })
                                        :
                                        <div style={{ marginTop: 12 }}>
                                            <div className={classes.suggestionInfo}>
                                                <span style={{ color: currentStatus === STATUS_ACCEPTED ? '#50832C' : '#DD2C00' }}>
                                                    {currentStatus === STATUS_ACCEPTED ? "Accepted by " : "Rejected by "}
                                                </span>
                                                {statusUsers ?
                                                    statusUsers.map((statusUser, statusIndex) => {
                                                        const statusUserData = users.find(user => user.value === statusUser);
                                                        const statusUserText = statusUserData ?
                                                            statusUserData.label
                                                            :
                                                            "User not available";
                                                        return <span key={statusIndex} data-test="status-user">
                                                            <PersonIcon className={classes.suggestionIcon} />
                                                            {statusUserText}
                                                        </span>;
                                                    })
                                                    :
                                                    <span data-test="status-user" style={{ marginRight: 12 }}><PersonIcon className={classes.suggestionIcon} /> API</span>
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                            {showDiscussion && hasDiscussion && exists
                                && (entitlements.includes(ENTITLEMENTS.CREATE_DISCUSSION) || entitlements.includes(ENTITLEMENTS.VIEW_DISCUSSION))
                                && hasDiscussion.map(discussion =>
                                    <div key={discussion} style={{ marginTop: 12 }}>
                                        <DiscussionViewer
                                            entitlements={entitlements}
                                            isReadOnly={isReadOnly || invalid || !isInScheme}
                                            active={currentStatus === STATUS_PENDING}
                                            defaultLang={defaultLang}
                                            key={discussion}
                                            id={discussion}
                                            graph={graph}
                                            users={users} />
                                    </div>
                                )}
                        </div>
                        }
                    </div>
                </div>
                {addNote &&
                    <AddSuggestionNoteDialog
                        optionForXL={optionForXL && (prefLabel || altLabel) && "suggestionLabelInsertIRI" in data ? true : false}
                        useXL={useXL}
                        accepted={accepted}
                        title={accepted ? "Reason for Acceptance" : "Reason for Rejection"}
                        defaultLang={defaultLang}
                        graph={graph}
                        useSlashIRI={useSlashIRI}
                        useCounterIRI={useCounterIRI}
                        counters={counters}
                        iriOptions={iriOptions}
                        suggestionType={suggestionType.value}
                        resourceIRI={data.suggestionInsertIRI}
                        labelResourceIRI={data.suggestionLabelInsertIRI}
                        schemeIRI={schemeIRI}
                        onCancelNote={this.onCancelNote}
                        onSkipNote={this.onSkipNote}
                        onAddNote={this.onAddNote} />
                }
                {isWarning && <Warning message={errorMessage} onExitWarning={this.onExitWarning} />}
                {isErrored && <Errored data={isErrored} />}
            </div>
        );
    }
}

Suggestion.defaultProps = {
    isReadOnly: false,
    showDiscussion: true,
    optionForXL: false,
    expanded: false,
    useCounterIRI: false,
    useXL: false
};

Suggestion.propTypes = {
    showDiscussion: PropTypes.bool,
    expanded: PropTypes.bool,
    components: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    optionForXL: PropTypes.bool,
    useXL: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    schemeIRI: PropTypes.string,
    resourceStatus: PropTypes.string,
    onResolution: PropTypes.func,
    handleStatusChange: PropTypes.func,
    projectIRI: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    users: PropTypes.array.isRequired,
    useSlashIRI: PropTypes.bool,
    useCounterIRI: PropTypes.bool,
    counters: PropTypes.object,
    entitlements: PropTypes.array,
    isReadOnly: PropTypes.bool,
    onToggleView: PropTypes.func,
    onUpdate: PropTypes.func,
    onAdd: PropTypes.func,
    classes: PropTypes.object,
    iriOptions: PropTypes.object
};

export default withRouter(withStyles(styles)(withGS(Suggestion, ["ALL"], null, {})));